import React, { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Infinity as InfinityIcon, ShieldCheck } from "lucide-react";

function formatApiErrorDetail(detail) {
  if (detail == null) return null;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  return typeof detail?.msg === "string" ? detail.msg : String(detail);
}

export default function AcceptInvitePage() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const [invite, setInvite] = useState(null);
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!token) {
      setErr("This link is missing its invitation code.");
      return;
    }
    api
      .get(`/auth/invite/${token}`)
      .then(({ data }) => setInvite(data))
      .catch((e) => setErr(formatApiErrorDetail(e?.response?.data?.detail) || e.message));
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (password !== confirm) {
      setErr("The two passwords do not match.");
      return;
    }
    setBusy(true);
    setErr(null);
    try {
      const { data } = await api.post("/auth/accept-invite", { token, password });
      setUser(data);
      navigate("/dashboard", { replace: true });
    } catch (e2) {
      setErr(formatApiErrorDetail(e2?.response?.data?.detail) || e2.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F3F8F7] flex items-center justify-center px-4">
      <div className="w-full max-w-md" data-testid="accept-invite-page">
        <div className="flex items-center gap-2 justify-center">
          <InfinityIcon className="h-7 w-7 text-[#156F5B]" strokeWidth={2.5} />
          <span className="text-2xl font-bold font-heading tracking-wide text-[#156F5B]">KOIOC</span>
        </div>
        <div className="mt-6 k-card p-8">
          <div className="k-label flex items-center gap-2"><ShieldCheck className="h-3.5 w-3.5" /> Invitation</div>
          <h1 className="mt-2 text-2xl font-heading font-bold text-[#212121]">Set your password</h1>

          {invite ? (
            <>
              <p className="mt-2 text-sm text-[#4D4D4D]" data-testid="accept-invite-who">
                Welcome {invite.name}. Your account <span className="font-semibold">{invite.email}</span> is ready —
                choose a password to finish.
              </p>
              <form onSubmit={submit} className="mt-6 space-y-4">
                <div>
                  <label className="k-sublabel">New password</label>
                  <input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    minLength={6}
                    required
                    data-testid="accept-invite-password"
                    className="k-input mt-1"
                  />
                </div>
                <div>
                  <label className="k-sublabel">Confirm password</label>
                  <input
                    type="password"
                    value={confirm}
                    onChange={(e) => setConfirm(e.target.value)}
                    minLength={6}
                    required
                    data-testid="accept-invite-confirm"
                    className="k-input mt-1"
                  />
                </div>
                {err && <div className="text-sm text-red-600" data-testid="accept-invite-error">{err}</div>}
                <button type="submit" disabled={busy} data-testid="accept-invite-submit" className="k-btn-primary w-full justify-center">
                  {busy ? "Setting up…" : "Set password and sign in"}
                </button>
              </form>
            </>
          ) : (
            <div className="mt-4">
              {err ? (
                <>
                  <p className="text-sm text-red-600" data-testid="accept-invite-error">{err}</p>
                  <p className="mt-4 text-sm text-[#6B7280]">
                    Ask your administrator to send a new invitation, or{" "}
                    <Link to="/login" className="text-[#156F5B] font-semibold hover:underline">sign in</Link> if you
                    already have an account.
                  </p>
                </>
              ) : (
                <p className="k-sublabel">Checking your invitation…</p>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
